import {
  AltArrowDownIcon,
  CircleMinusIcon,
  CirclePlusIcon,
  DragHandleIcon,
} from "@cynthai/icons";
import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  Button as RacButton,
  Collection,
  DropIndicator,
  Tree,
  TreeItem,
  TreeItemContent,
  useDragAndDrop,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { ScrollArea } from "../ScrollArea";
import { Skeleton } from "../Skeleton";
import type { SlotClassNames } from "../types";
import { EditableLabel } from "./EditableLabel";
import { CONNECTOR_NODE_SPACING, RowConnectors } from "./RowConnectors";
import type {
  TreeDropPosition,
  TreeReorderParams,
  TreeViewNode,
} from "./types";

const LOADING_SKELETON_KEYS = ["tree-node-1", "tree-node-2", "tree-node-3"];
const TREE_NODE_DRAG_TYPE = "application/x-cynthai-tree-node";

// rac flattens the tree: every row (children included) is a direct sibling in
// the treegrid container, so visual grouping is expressed per row: top-level
// rows open a group (margin + top rounding), the last rendered row of a group
// closes it (bottom rounding)
const treeViewStyles = tv({
  slots: {
    base: "flex h-full flex-col rounded-lg",
    item: `
      bg-secondary data-[drop-target]:outline-primary relative
      data-[drop-target]:outline-2 data-[drop-target]:-outline-offset-2
    `,
    row: "relative flex",
    // wraps the indent, expander and label when cells follow them, so a table
    // can pin that block while its cells scroll
    leading: "flex min-w-0 flex-1",
    // the row state lives on the label block, so cells beside it repeat it
    cells: `
      flex shrink-0 transition-colors data-[dragging=true]:opacity-50
      data-[selected=true]:bg-background-primary/50
    `,
    scrollArea: "h-full",
    expander: "flex items-center justify-center",
    skeletonRow: `
      bg-secondary flex items-center gap-4 rounded-lg border border-transparent
      px-4
    `,
  },
  variants: {
    // `comfortable` renders rows as separated cards; `compact` renders one
    // continuous surface with hairline separators
    density: {
      comfortable: {
        base: "gap-2 pt-4",
        item: `
          data-[root-gap=true]:mt-2 data-[level=1]:rounded-t-lg
          data-[tail=true]:rounded-b-lg
        `,
        row: "min-h-14",
        expander: "size-6",
        skeletonRow: "h-14",
      },
      compact: {
        item: "border-border-table/50 border-b",
        // the separator is a border on the item, so the row has to give back
        // its 1px to keep the row pitch at exactly 44px
        row: "min-h-[calc(2.75rem-1px)]",
        expander: "size-6",
        skeletonRow: "h-11 rounded-none",
      },
      dense: {
        item: "border-border-table/50 border-b",
        // same 1px give-back as compact, at the 28px data-table pitch
        row: "min-h-[calc(1.75rem-1px)]",
        expander: "size-4",
        skeletonRow: "h-7 rounded-none",
      },
    },
  },
  defaultVariants: {
    density: "comfortable",
  },
});

export type TreeViewStyles = ReturnType<typeof treeViewStyles>;

export type TreeViewDensity = "comfortable" | "compact" | "dense";

// `circle` draws connector lines between levels; `chevron` drops them and
// indents by a fixed step instead
export type TreeViewExpanderStyle = "circle" | "chevron";

const CHEVRON_INDENT = 24;

export type TreeViewProps<T> = {
  readonly "aria-label": string;
  readonly data: TreeViewNode<T>[];
  readonly density?: TreeViewDensity;
  readonly expanderStyle?: TreeViewExpanderStyle;
  readonly header?: ReactNode;
  readonly isLoading?: boolean;
  readonly emptyState?: ReactNode;
  readonly classNames?: SlotClassNames<TreeViewStyles>;
  readonly selectedId?: string | null;
  readonly onSelect?: (node: TreeViewNode<T>) => void;
  readonly expandedIds?: Set<string>;
  readonly defaultExpandedIds?: Set<string>;
  readonly onExpandedChange?: (expandedIds: Set<string>) => void;
  readonly onRename?: (node: TreeViewNode<T>, label: string) => void;
  readonly renderIcon?: (node: TreeViewNode<T>) => ReactNode;
  // cells rendered beside the label block instead of inside it
  readonly renderCells?: (node: TreeViewNode<T>) => ReactNode;
  readonly renderRow?: (
    node: TreeViewNode<T>,
    parent: TreeViewNode<T> | null,
    defaultLabel: ReactNode,
  ) => ReactNode;
  readonly renderSelectionCell?: (
    node: TreeViewNode<T>,
    parent: TreeViewNode<T> | null,
  ) => ReactNode;
  readonly renderEmptyChildren?: (node: TreeViewNode<T>) => ReactNode;
  readonly canReorder?: boolean;
  readonly lastLeafMargin?: number;
  readonly isValidDrop?: (
    dragged: TreeViewNode<T>,
    target: TreeViewNode<T>,
    position: TreeDropPosition,
  ) => boolean;
  readonly onReorder?: (params: TreeReorderParams<T>) => void;
};

export function TreeView<T>({
  data,
  density = "comfortable",
  expanderStyle = "circle",
  header,
  isLoading,
  emptyState,
  classNames,
  selectedId,
  onSelect,
  expandedIds,
  defaultExpandedIds,
  onExpandedChange,
  onRename,
  renderIcon,
  renderRow,
  renderCells,
  renderSelectionCell,
  renderEmptyChildren,
  canReorder = false,
  lastLeafMargin,
  isValidDrop,
  onReorder,
  ...props
}: TreeViewProps<T>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<
    Set<string>
  >(defaultExpandedIds ?? new Set());
  const draggedIdRef = useRef<string | null>(null);

  // rac only reports isExpanded for rows with loaded children, so expansion is
  // tracked here to also cover empty expandable nodes (chevron + placeholder)
  const expanded = expandedIds ?? uncontrolledExpandedIds;
  const handleExpandedChange = (keys: Iterable<unknown>) => {
    const next = new Set([...keys].map(String));
    if (!expandedIds) setUncontrolledExpandedIds(next);
    onExpandedChange?.(next);
  };

  const { nodeById, parentById } = useMemo(() => {
    const nodeById = new Map<string, TreeViewNode<T>>();
    const parentById = new Map<string, TreeViewNode<T> | null>();
    const walk = (nodes: TreeViewNode<T>[], parent: TreeViewNode<T> | null) => {
      for (const node of nodes) {
        nodeById.set(node.id, node);
        parentById.set(node.id, parent);
        if (node.children) walk(node.children, node);
      }
    };
    walk(data, null);
    return { nodeById, parentById };
  }, [data]);

  const levelOf = (id: string) => {
    let level = 0;
    let parent = parentById.get(id);
    while (parent) {
      level++;
      parent = parentById.get(parent.id);
    }
    return level;
  };

  const reorderEnabled = canReorder && !!onReorder;
  const isChevron = expanderStyle === "chevron";
  const indent = isChevron ? CHEVRON_INDENT : CONNECTOR_NODE_SPACING;

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) => {
      draggedIdRef.current = String([...keys][0]);
      return [...keys].map((key) => ({ [TREE_NODE_DRAG_TYPE]: String(key) }));
    },
    acceptedDragTypes: [TREE_NODE_DRAG_TYPE],
    getAllowedDropOperations: () => ["move"],
    getDropOperation: (target) => {
      if (target.type !== "item") {
        return "cancel";
      }
      const dragged = draggedIdRef.current
        ? nodeById.get(draggedIdRef.current)
        : null;
      const targetNode = nodeById.get(String(target.key));
      if (!dragged || !targetNode || dragged.id === targetNode.id) {
        return "cancel";
      }
      if (
        isValidDrop &&
        !isValidDrop(dragged, targetNode, target.dropPosition)
      ) {
        return "cancel";
      }
      return "move";
    },
    // onMove receives every drop in the tree, siblings included, and onReorder
    // only sibling drops; wiring both would report a sibling drop twice.
    // isValidDrop is what keeps cross-parent drops out where they are unwanted
    onMove: (e) => {
      const dragged = nodeById.get(String([...e.keys][0]));
      const target = nodeById.get(String(e.target.key));
      if (!dragged || !target) return;
      onReorder?.({
        dragged,
        target,
        parent:
          e.target.dropPosition === "on"
            ? target
            : (parentById.get(target.id) ?? null),
        position: e.target.dropPosition,
      });
    },
    onDragEnd: () => {
      draggedIdRef.current = null;
    },
    renderDropIndicator: (target) => (
      <DropIndicator
        target={target}
        className={({ isDropTarget }) =>
          isDropTarget ? "bg-primary z-10 h-0.5" : "h-0"
        }
        style={{
          marginLeft:
            target.type === "item" ? levelOf(String(target.key)) * indent : 0,
        }}
      />
    ),
  });

  const styles = treeViewStyles({ density });
  const expanderIconSize = density === "dense" ? 16 : 24;

  // rac caches rendered rows by item identity; anything read inside renderNode
  // that can change without the data changing must invalidate that cache
  const renderDependencies = [
    editingId,
    selectedId,
    expanded,
    onSelect,
    onRename,
    renderIcon,
    renderRow,
    renderCells,
    renderSelectionCell,
    renderEmptyChildren,
    reorderEnabled,
    classNames?.item,
    classNames?.row,
    classNames?.leading,
    classNames?.cells,
    classNames?.expander,
    lastLeafMargin,
    density,
    expanderStyle,
  ];

  const renderNode = (
    node: TreeViewNode<T>,
    parent: TreeViewNode<T> | null,
    parentConnectors: boolean[],
  ): ReactNode => {
    const level = parentConnectors.length;
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const isExpandable = hasChildren || !!node.canExpand;
    const isExpanded = isExpandable && expanded.has(node.id);
    const isSelectable = !!node.canSelect && !!onSelect;
    // last rendered row of its top-level group: every ancestor is a last
    // child and no expanded children follow below this row
    const isGroupTail =
      parentConnectors.every((connector) => !connector) &&
      !(isExpanded && hasChildren);

    const defaultLabel = (
      <div className="flex min-w-0 flex-1 items-center select-none">
        {onRename ? (
          <EditableLabel
            value={node.label}
            isEditing={editingId === node.id}
            onStartEdit={() => setEditingId(node.id)}
            onCommit={(label) => {
              onRename(node, label);
              setEditingId(null);
            }}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <span className="text-text-primary text-sm">{node.label}</span>
        )}
      </div>
    );

    const renderLabelBlock = (isDragging: boolean | undefined) => (
      <div
        className={`data-[selected=true]:border-primary/50 data-[selected=true]:bg-background-primary/50 relative flex min-w-0 flex-1 items-center gap-4 rounded-lg border border-transparent px-4 transition-colors data-[dragging=true]:opacity-50 data-[expander=chevron]:gap-2 data-[selectable=true]:cursor-pointer`}
        style={{
          marginLeft: !hasChildren
            ? (lastLeafMargin ?? level * indent)
            : level * indent,
        }}
        data-expander={expanderStyle}
        data-selected={selectedId === node.id}
        data-dragging={isDragging}
        data-selectable={isSelectable}
      >
        {isExpandable && (
          <div
            className={styles.expander({
              className: classNames?.expander,
            })}
          >
            <RacButton
              slot="chevron"
              className="flex cursor-pointer items-center justify-center rounded-full transition-colors"
            >
              {isChevron ? (
                <AltArrowDownIcon
                  width={expanderIconSize}
                  height={expanderIconSize}
                  data-expanded={isExpanded}
                  className="text-text-primary data-[expanded=true]:text-primary data-[expanded=false]:-rotate-90"
                />
              ) : isExpanded ? (
                <CircleMinusIcon
                  active
                  width={expanderIconSize}
                  height={expanderIconSize}
                />
              ) : (
                <CirclePlusIcon
                  width={expanderIconSize}
                  height={expanderIconSize}
                  className="text-text-primary"
                />
              )}
            </RacButton>
          </div>
        )}

        {renderIcon?.(node)}

        {renderRow ? renderRow(node, parent, defaultLabel) : defaultLabel}
      </div>
    );

    return (
      <TreeItem
        id={node.id}
        textValue={node.label}
        hasChildItems={isExpandable}
        onAction={isSelectable ? () => onSelect?.(node) : undefined}
        className={styles.item({ className: classNames?.item })}
        data-tail={isGroupTail || undefined}
        data-root-gap={
          // dnd renders hidden drop indicators as siblings, so a first-child
          // selector can't identify the first row: computed here instead
          (level === 0 && data[0]?.id !== node.id) || undefined
        }
      >
        <TreeItemContent>
          {({ isDragging }) => (
            <>
              {reorderEnabled && level > 0 && <hr className="border-border" />}

              <div
                className={styles.row({
                  className: twMerge(reorderEnabled && "pl-4", classNames?.row),
                })}
              >
                {reorderEnabled && (
                  <RacButton
                    slot="drag"
                    className={`flex cursor-grab items-center justify-center transition-opacity active:cursor-grabbing`}
                    aria-label="drag to reorder"
                  >
                    <DragHandleIcon />
                  </RacButton>
                )}

                {renderSelectionCell ? (
                  <div className="flex shrink-0 items-center justify-center px-4">
                    {renderSelectionCell(node, parent)}
                  </div>
                ) : null}

                {!isChevron && (
                  <RowConnectors
                    level={level}
                    parentConnectors={parentConnectors}
                    canReorder={reorderEnabled}
                    hasExpandedChildren={hasChildren && isExpanded}
                    hasSelectionCell={!!renderSelectionCell}
                  />
                )}

                {renderCells ? (
                  <>
                    <div
                      className={styles.leading({
                        className: classNames?.leading,
                      })}
                    >
                      {renderLabelBlock(isDragging)}
                    </div>
                    <div
                      className={styles.cells({
                        className: classNames?.cells,
                      })}
                      data-selected={selectedId === node.id}
                      data-dragging={isDragging}
                    >
                      {renderCells(node)}
                    </div>
                  </>
                ) : (
                  renderLabelBlock(isDragging)
                )}
              </div>

              {!hasChildren && isExpanded && renderEmptyChildren && (
                <div className="relative">
                  <div
                    className="flex items-center py-3"
                    style={{
                      marginLeft: (level + 1) * indent,
                    }}
                  >
                    {renderEmptyChildren(node)}
                  </div>
                </div>
              )}
            </>
          )}
        </TreeItemContent>

        {hasChildren && (
          <Collection items={children} dependencies={renderDependencies}>
            {(child) => {
              const isLast = children.at(-1)?.id === child.id;
              return renderNode(child, node, [...parentConnectors, !isLast]);
            }}
          </Collection>
        )}
      </TreeItem>
    );
  };

  return (
    <div className={styles.base({ className: classNames?.base })}>
      {header}

      <ScrollArea
        className={styles.scrollArea({ className: classNames?.scrollArea })}
        orientation="vertical"
      >
        {isLoading ? (
          <div className="space-y-2">
            {LOADING_SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className={styles.skeletonRow({
                  className: classNames?.skeletonRow,
                })}
              >
                <Skeleton className="bg-background h-6 w-4 rounded-sm" />
                <Skeleton className="bg-background size-6 rounded-sm" />
                <Skeleton className="bg-background h-6 flex-1 rounded-sm" />
              </div>
            ))}
          </div>
        ) : data.length > 0 ? (
          <Tree
            aria-label={props["aria-label"]}
            items={data}
            dependencies={renderDependencies}
            expandedKeys={expanded}
            onExpandedChange={handleExpandedChange}
            dragAndDropHooks={reorderEnabled ? dragAndDropHooks : undefined}
            className="flex min-h-0 flex-1 flex-col outline-none"
          >
            {(node) => renderNode(node, null, [])}
          </Tree>
        ) : (
          (emptyState ?? null)
        )}
      </ScrollArea>
    </div>
  );
}
